import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GridColumns } from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import Table, { TableProps } from 'src/components/Table';
import {
  IngramProgram,
  useGetIngramPrograms,
} from '../hooks/ingramProgramsSearch';
import { Tag } from '@im/ui-lib/Tag';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import { NavLink } from '@im/ui-lib/NavLink';
import { useSearchParams } from 'react-router-dom';
import ProgramForm from '../components/ProgramForm';
import { renderCellWithArray } from '../util/renderCellWithArray';
import {
  TextField,
  InputAdornment,
  IconButton,
  SearchIcon,
} from '../components/SearchBarWithChips';
import useDebouncedState from 'src/hooks/useDebouncedState';
import ExportComponent from '../components/ExportComponent';

const IngramPrograms: React.FC = () => {
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('companyCode');
  const countryCode = searchParams.get('countryCode');

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [ingramProgramName, setIngramProgramName] = useState<string>('');

  const requestBody = {
    ...(Boolean(ingramProgramName) && {
      IngramProgramName: ingramProgramName,
    }),
    page,
    pageSize,
  };

  const { data: { ingramProgramList, totalCount } = {}, isLoading } =
    useGetIngramPrograms(useDebouncedState(requestBody, 300));

  const handleOnChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { value } = event.target;
    setIngramProgramName(value);
  };
  const columns = useMemo<GridColumns>(
    (): GridColumns<IngramProgram> => [
      {
        field: 'ingramProgramName',
        flex: 1.5,
        headerName: translate('IngramProgram.ProgramName'),
        minWidth: 140,
        renderCell: ({ row }) => (
          <NavLink
            data-id={row.ingramProgramId}
            data-name={row.ingramProgramName}
            data-testid={row.ingramProgramName}
            navItem={{
              key: row.ingramProgramName,
              label: row.ingramProgramName,
              route: `${row.ingramProgramId.toString()}?countryCode=${countryCode}&companyCode=${companyCode}`,
            }}
            sx={(theme) => ({
              color: theme.palette.primary[700],
            })}
          />
        ),
        sortable: false,
      },
      {
        field: 'activeDiscount',
        flex: 0.75,
        headerName: translate('IngramProgram.ActiveDiscounts'),
        minWidth: 100,
        renderCell: ({ row }) => (
          <Tag
            label={translate(
              `IngramProgram.${row.activeDiscount ? 'Active' : 'Deactive'}`,
              row.activeDiscount && { count: row.activeDiscount }
            )}
            variant={row.activeDiscount ? 'success' : 'default'}
          />
        ),
        sortable: false,
      },
      {
        field: 'programType',
        flex: 1,
        headerName: translate('IngramProgram.ProgramType'),
        minWidth: 140,
        sortable: false,
        valueGetter: ({ row }) => row.programType,
      },
      {
        field: 'product_group_list',
        flex: 1.5,
        headerName: translate('IngramProgram.ProductGroup'),
        minWidth: 100,
        renderCell: ({ row }) => {
          const options = row.product_group_list.map(
            (group) => group.productGroupName
          );
          return renderCellWithArray(options);
        },
        sortable: false,
      },
      {
        field: 'customer_group_list',
        flex: 1.5,
        headerName: translate('IngramProgram.CustomerGroup'),
        minWidth: 100,
        renderCell: ({ row }) => {
          const options = row.customer_group_list.map(
            (group) => group.customergroupname
          );
          return renderCellWithArray(options);
        },
        sortable: false,
      },
      {
        field: 'lastChangeDate',
        flex: 0.75,
        headerName: translate('IngramProgram.LastModified'),
        minWidth: 100,
        sortable: false,
        valueGetter: ({ row }) => formatDateTime(row.lastChangeDate),
      },
    ],
    [companyCode, countryCode, formatDateTime, translate]
  );

  const tableProps: TableProps<IngramProgram> = {
    columns,
    getRowId: (row) => row.ingramProgramId,
    loading: isLoading,
    pagination: {
      page,
      pageSize,
      setPage,
      setPageSize,
    },
    rows: ingramProgramList || [],
    totalCount,
  };
  return (
    <Box p={2}>
      <Stack gap={2}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography
            sx={{ fontSize: '30px', fontWeight: 600, lineHeight: '38px' }}
          >
            {translate('IngramProgram.Title')}
          </Typography>
          <ProgramForm buttonName={translate('IngramProgram.AddNewProgram')} />
        </Stack>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            name="ingramProgramsSearchField"
            onChange={handleOnChange}
            placeholder={translate('IngramProgram.ProgramNamePlaceholder')}
            sx={{ width: '30%' }}
            value={ingramProgramName}
            variant="outlined"
          />
          <ExportComponent isProgramOrDiscount={true} params={requestBody} />
        </Stack>
        <Table {...tableProps} />
      </Stack>
    </Box>
  );
};

export default IngramPrograms;
